import { inject, Injectable } from '@angular/core';
import { Dashboard, DashboardJson } from '../models/dashboard';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ConfigService } from 'src/app/shared/services/config-service';
import { resolveDashboard, resolveDashboards, serializeDashboard } from '../resolvers/dashboard-json.resolver';
import { UserStore } from 'src/app/shared/user/store/user.store';

@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  // HttpClient via Angular-Injektion holen
  private http = inject(HttpClient);
  // ConfigService via Angular-Injektion holen (liefert Basis-URLs etc.)
  private configService = inject(ConfigService);
  private userStore = inject(UserStore);

  /**
   * Holt alle Dashboards des aktuell angemeldeten Users vom Backend.
   * 
   * @returns Observable mit einem Array von Dashboard-Instanzen
   */
  public fetchUserDashboards(): Observable<Dashboard[]> {
    // Baut die URL aus Basis-URL und Endpunkt "dashboard/by-user"
    const url = `${this.configService.getUrlNectaOneApi()}/${this.userStore.activeTenant()?.tenantId}/dashboard/by-user`;
    return this.http.get<DashboardJson[]>(url).pipe(
      // Wandelt das JSON-Array in Dashboard-Objekte um
      map(jsonArray => resolveDashboards(jsonArray))
    );
  }

  /**
   * Holt alle Dashboards des Tenants vom Backend.
   * 
   * @returns Observable mit einem Array von Dashboard-Instanzen
   */
  public fetchDashboardsForTenant(): Observable<Dashboard[]> {
    const url = `${this.configService.getUrlNectaOneApi()}/${this.userStore.activeTenant()?.tenantId}/dashboard/by-tenant`;
    return this.http.get<DashboardJson[]>(url).pipe(
      // Wandelt das JSON-Array in Dashboard-Objekte um
      map(jsonArray => resolveDashboards(jsonArray))
    );
  }

  /**
   * Holt ein einzelnes Dashboard anhand seiner ID vom Backend.
   * 
   * @param id Die ID des Dashboards, das abgefragt werden soll
   * @returns Observable mit dem entsprechenden Dashboard-Objekt
   */
  public fetchDashboardById(id: number): Observable<Dashboard> {
    const url = `${this.configService.getUrlNectaOneApi()}/${this.userStore.activeTenant()?.tenantId}/dashboard/${id}/by-id`;
    return this.http.get<DashboardJson>(url).pipe(
      // Wandelt das einzelne DashboardJson-Objekt in eine Dashboard-Instanz um
      map(json => resolveDashboard(json))
    );
  }

  /**
   * Legt ein neues Dashboard im Backend an.
   * 
   * @param dashboard Das zu erstellende Dashboard-Objekt
   * @returns Observable mit dem neu erstellten Dashboard (inkl. ggf. zugewiesener ID vom Server)
   */
  public createDashboard(dashboard: Dashboard): Observable<Dashboard> {
    const url = `${this.configService.getUrlNectaOneApi()}/${this.userStore.activeTenant()?.tenantId}/dashboard`;
    // serialisiere das Dashboard-Objekt in DashboardJson, bevor es gesendet wird
    const payload: DashboardJson = serializeDashboard(dashboard);
    return this.http.post<DashboardJson>(url, payload).pipe(
      // Wandelt das zurückgegebene JSON in eine Dashboard-Instanz um
      map(json => resolveDashboard(json))
    );
  }

  /**
   * Aktualisiert ein existierendes Dashboard im Backend.
   * 
   * @param dashboard    Das Dashboard-Objekt mit geänderten Werten
   * @param dashboardId  Die ID des Dashboards, das aktualisiert werden soll
   * @returns Observable mit dem aktualisierten Dashboard-Objekt
   */
  public updateDashboard(dashboard: Dashboard, dashboardId: number): Observable<Dashboard> {
    const url = `${this.configService.getUrlNectaOneApi()}/${this.userStore.activeTenant()?.tenantId}/dashboard/${dashboardId}`;
    // serialisiere das geänderte Dashboard-Objekt in DashboardJson
    const payload: DashboardJson = serializeDashboard(dashboard);
    return this.http.put<DashboardJson>(url, payload).pipe(
      // Wandelt das zurückgegebene JSON in eine Dashboard-Instanz um
      map(json => resolveDashboard(json))
    );
  }

  /**
   * Löscht ein Dashboard anhand seiner ID im Backend.
   * 
   * @param dashboardId Die ID des Dashboards, das gelöscht werden soll
   * @returns Observable mit einem boolean (true, wenn Löschung erfolgreich)
   */
  public deleteDashboard(dashboardId: number): Observable<boolean> {
    const url = `${this.configService.getUrlNectaOneApi()}/${this.userStore.activeTenant()?.tenantId}/dashboard/${dashboardId}`;
    return this.http.delete<boolean>(url).pipe(
      // Leitet den boolean-Wert direkt weiter
      map(result => result)
    );
  }
}
